import {
  rewardTasksAtom,
  setRewardTasksUnclaimedCount,
} from "@app/gamification/atoms/reward-tasks.atom";
import { URLS } from "@core/shared/utils/urls";
import { useIsLoggedIn } from "@framework/account/hooks";
import { userAtom } from "@framework/config/atoms";
import { platformFeaturesAtom } from "@framework/shared/atoms";
import { useEffect } from "react";

/** Skip refreshes closer together than this (several components share one count). */
const minimumRefreshIntervalMs = 30_000;

let inFlightRequest: Promise<void> | null = null;
let lastRequestedAt = 0;

/**
 * Refresh the shared unclaimed reward task count, deduplicating concurrent and
 * recent requests unless `force` is set.
 *
 * @example
 * void refreshRewardTasksUnclaimedCount();
 */
export function refreshRewardTasksUnclaimedCount(force = false): Promise<void> {
  if (inFlightRequest) return inFlightRequest;

  if (!force && Date.now() - lastRequestedAt < minimumRefreshIntervalMs) {
    return Promise.resolve();
  }

  lastRequestedAt = Date.now();
  inFlightRequest = fetch(URLS.gamificationTasks.unclaimedCount, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((payload: { unclaimedCount?: unknown } | null) => {
      if (typeof payload?.unclaimedCount === "number") {
        setRewardTasksUnclaimedCount(payload.unclaimedCount);
      }
    })
    .catch(() => undefined)
    .finally(() => {
      inFlightRequest = null;
    });

  return inFlightRequest;
}

/**
 * Whether the current viewer can see reward tasks (logged-in student with
 * Gamification released).
 *
 * @example
 * const enabled = useRewardTasksEnabled();
 */
export function useRewardTasksEnabled(): boolean {
  const isLoggedIn = useIsLoggedIn();
  const user = userAtom.useValue();
  const gamificationEnabled = platformFeaturesAtom.useValue().gamification;

  return isLoggedIn && user?.isStudent === true && gamificationEnabled === true;
}

/**
 * Unclaimed reward task count, loaded on mount and refreshed on window focus.
 * Returns 0 and never requests while reward tasks are not available.
 *
 * @example
 * const { unclaimedCount } = useRewardTasksUnclaimedCount();
 */
export function useRewardTasksUnclaimedCount() {
  const isEnabled = useRewardTasksEnabled();
  const { unclaimedCount } = rewardTasksAtom.useValue();

  useEffect(() => {
    if (!isEnabled) return;

    void refreshRewardTasksUnclaimedCount();

    const refresh = () => {
      void refreshRewardTasksUnclaimedCount();
    };

    window.addEventListener("focus", refresh);

    return () => window.removeEventListener("focus", refresh);
  }, [isEnabled]);

  return {
    isEnabled,
    unclaimedCount: isEnabled ? unclaimedCount : 0,
  };
}
